import { useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Switch from "@mui/material/Switch";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import ActionDialog from "components/admin/ActionDialog";
import IconPicker from "components/admin/IconPicker";
import { useRunner } from "components/admin/hooks";
import useConfirmChanges from "components/admin/confirmChanges";
import api from "api/client";
import { itemTypeLabel, elementLabel, nclassLabel } from "layouts/templates/labels";
import OptionEditor, { optionInputError } from "layouts/templates/OptionEditor";

const DAY_MS = 86400000;

const folderOf = (path, fallback) => (path && path.includes("/") ? path.split("/")[0] : fallback);

function NumberField({ label, value, onChange, helperText }) {
  return (
    <MDInput
      fullWidth
      type="number"
      label={label}
      value={value}
      helperText={helperText}
      inputProps={{ min: 0 }}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

NumberField.defaultProps = { helperText: null };
NumberField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onChange: PropTypes.func.isRequired,
  helperText: PropTypes.string,
};

const toNumber = (value) => Number(value) || 0;

/** Khoảng chỉ số ngẫu nhiên khi tạo trang bị: để trống cả 2 ô là không có chỉ số này. */
function RangeField({ label, value, onChange }) {
  const [min, max] = value;
  return (
    <MDBox display="flex" gap={1} alignItems="center">
      <MDInput
        fullWidth
        type="number"
        label={`${label} từ`}
        value={min}
        inputProps={{ min: 0 }}
        onChange={(event) => onChange([event.target.value, max])}
      />
      <MDInput
        fullWidth
        type="number"
        label="đến"
        value={max}
        inputProps={{ min: 0 }}
        onChange={(event) => onChange([min, event.target.value])}
      />
    </MDBox>
  );
}

RangeField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])).isRequired,
  onChange: PropTypes.func.isRequired,
};

const rangeForm = (range) => (range && range.length ? [range[0], range[1] ?? range[0]] : ["", ""]);

function rangeBody([min, max]) {
  if (min === "" && max === "") return null;
  const a = toNumber(min === "" ? max : min);
  const b = toNumber(max === "" ? min : max);
  return [a, b];
}

const RANGES = [
  { key: "atkRange", label: "Công" },
  { key: "defRange", label: "Thủ" },
  { key: "hpRange", label: "HP" },
  { key: "mpRange", label: "MP" },
];

export function ItemEditDialog({ item, onClose, onDone }) {
  const [run, busy] = useRunner();
  const ask = useConfirmChanges();
  const [form, setForm] = useState({
    name: item.name || "",
    description: item.description || "",
    iconPath: item.iconPath || "",
    price: item.price ?? 0,
    canTrade: Boolean(item.canTrade),
    requireStr: item.requireStr ?? 0,
    requireAgi: item.requireAgi ?? 0,
    requireInt: item.requireInt ?? 0,
    expireDays: item.expire ? Math.round(item.expire / DAY_MS) : 0,
    ...Object.fromEntries(RANGES.map((r) => [r.key, rangeForm(item[r.key])])),
    itemOption: (item.itemOption || []).map(String),
    itemOptionValue: (item.itemOptionValue || []).map(String),
  });
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const hasStats = item.isEquip || RANGES.some((r) => item[r.key]?.length);
  const hasOptions = !item.isEquip;
  const optionError = optionInputError(form.itemOption, form.itemOptionValue);

  const submit = async () => {
    const yes = await ask({
      title: `Lưu vật phẩm ${item.name}`,
      before: {
        name: item.name || "",
        description: item.description || "",
        iconPath: item.iconPath || "",
        price: item.price ?? 0,
        canTrade: Boolean(item.canTrade),
        requireStr: item.requireStr ?? 0,
        requireAgi: item.requireAgi ?? 0,
        requireInt: item.requireInt ?? 0,
        expireDays: item.expire ? Math.round(item.expire / DAY_MS) : 0,
        ...Object.fromEntries(RANGES.map((r) => [r.key, rangeForm(item[r.key])])),
        itemOption: (item.itemOption || []).map(String),
        itemOptionValue: (item.itemOptionValue || []).map(String),
      },
      after: form,
      fields: [
        ["name", "Tên"],
        ["description", "Mô tả"],
        ["iconPath", "Hình"],
        ["price", "Giá bán cho NPC"],
        ["canTrade", "Cho giao dịch"],
        ["requireStr", "Yêu cầu sức"],
        ["requireAgi", "Yêu cầu nhanh"],
        ["requireInt", "Yêu cầu trí"],
        ["expireDays", "Hạn dùng (ngày)"],
        ...RANGES.map((r) => [r.key, `Dải ${r.label} (chỉ món mới)`]),
        ["itemOption", "Dòng option"],
        ["itemOptionValue", "Giá trị option (cả món đang có)"],
      ],
      notes: [
        "Tên, hình, giá, giao dịch, option: đổi ngay cho mọi người chơi.",
        "Dải công / thủ / HP / MP: chỉ món tạo ra sau khi lưu; món đang có giữ chỉ số cũ.",
      ],
    });
    if (!yes) return;
    const body = {
      ...form,
      price: toNumber(form.price),
      requireStr: toNumber(form.requireStr),
      requireAgi: toNumber(form.requireAgi),
      requireInt: toNumber(form.requireInt),
      expireDays: toNumber(form.expireDays),
      ...Object.fromEntries(RANGES.map((r) => [r.key, rangeBody(form[r.key])])),
      itemOption: form.itemOption.map(Number),
      itemOptionValue: form.itemOptionValue.map(Number),
    };
    const { ok } = await run(
      () => api.put(`/api/admin/templates/items/${item.itemId}`, body),
      "Đã lưu vật phẩm"
    );
    if (ok) {
      onClose();
      onDone();
    }
  };

  return (
    <ActionDialog
      open
      title={`Sửa vật phẩm: ${item.name} (#${item.itemId})`}
      color="warning"
      submitText="Lưu…"
      maxWidth="md"
      busy={busy}
      disabled={!form.name.trim() || !form.iconPath || Boolean(optionError)}
      onClose={onClose}
      onSubmit={submit}
    >
      <Alert severity="info">
        Áp dụng ngay cho mọi người chơi. Dải công / thủ / HP / MP chỉ tính cho món tạo ra sau khi
        lưu (món đã có giữ chỉ số đã random); dòng option (ngọc, % và hiệu ứng của cánh / cải trang)
        áp dụng cả cho món đang có. Loại vật phẩm ({itemTypeLabel(item.type)}) và cộng dồn không sửa
        được ở đây.
      </Alert>
      <IconPicker
        folder={folderOf(form.iconPath, "items")}
        value={form.iconPath}
        onChange={(iconPath) => set({ iconPath })}
      />
      <MDInput
        fullWidth
        label="Tên"
        value={form.name}
        onChange={(e) => set({ name: e.target.value })}
      />
      <MDInput
        fullWidth
        multiline
        minRows={2}
        label="Mô tả"
        value={form.description}
        onChange={(e) => set({ description: e.target.value })}
      />
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={6} sm={4}>
          <NumberField
            label="Giá sàn (kiosk)"
            value={form.price}
            onChange={(price) => set({ price })}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <NumberField
            label="Hạn dùng (ngày)"
            value={form.expireDays}
            helperText="0 = không hết hạn"
            onChange={(expireDays) => set({ expireDays })}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <MDBox display="flex" alignItems="center">
            <Switch checked={form.canTrade} onChange={(e) => set({ canTrade: e.target.checked })} />
            <MDTypography variant="button">Cho phép giao dịch</MDTypography>
          </MDBox>
        </Grid>
      </Grid>
      {hasStats && (
        <>
          <MDTypography variant="button" fontWeight="medium">
            Yêu cầu chỉ số pet để mặc
          </MDTypography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <NumberField
                label="Sức"
                value={form.requireStr}
                onChange={(v) => set({ requireStr: v })}
              />
            </Grid>
            <Grid item xs={4}>
              <NumberField
                label="Nhanh"
                value={form.requireAgi}
                onChange={(v) => set({ requireAgi: v })}
              />
            </Grid>
            <Grid item xs={4}>
              <NumberField
                label="Trí"
                value={form.requireInt}
                onChange={(v) => set({ requireInt: v })}
              />
            </Grid>
          </Grid>
          <MDTypography variant="button" fontWeight="medium">
            Chỉ số ngẫu nhiên khi tạo ra (để trống nếu không có)
          </MDTypography>
          <Grid container spacing={2}>
            {RANGES.map((r) => (
              <Grid item xs={12} sm={6} key={r.key}>
                <RangeField
                  label={r.label}
                  value={form[r.key]}
                  onChange={(value) => set({ [r.key]: value })}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}
      {hasOptions && (
        <OptionEditor
          type={item.type}
          options={form.itemOption}
          values={form.itemOptionValue}
          onChange={(itemOption, itemOptionValue) => set({ itemOption, itemOptionValue })}
        />
      )}
    </ActionDialog>
  );
}

ItemEditDialog.propTypes = {
  item: PropTypes.shape({
    itemId: PropTypes.number.isRequired,
    name: PropTypes.string,
    description: PropTypes.string,
    iconPath: PropTypes.string,
    type: PropTypes.number,
    price: PropTypes.number,
    canTrade: PropTypes.bool,
    isEquip: PropTypes.bool,
    requireStr: PropTypes.number,
    requireAgi: PropTypes.number,
    requireInt: PropTypes.number,
    expire: PropTypes.number,
    atkRange: PropTypes.arrayOf(PropTypes.number),
    defRange: PropTypes.arrayOf(PropTypes.number),
    hpRange: PropTypes.arrayOf(PropTypes.number),
    mpRange: PropTypes.arrayOf(PropTypes.number),
    itemOption: PropTypes.arrayOf(PropTypes.number),
    itemOptionValue: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

export function PetEditDialog({ pet, onClose, onDone }) {
  const [run, busy] = useRunner();
  const ask = useConfirmChanges();
  const [form, setForm] = useState({
    name: pet.name || "",
    icon: pet.icon || "",
    str: pet.str ?? 0,
    agi: pet.agi ?? 0,
    _int: pet._int ?? 0,
  });
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const submit = async () => {
    const yes = await ask({
      title: `Lưu pet ${pet.name}`,
      before: pet,
      after: form,
      fields: [
        ["name", "Tên"],
        ["icon", "Hình"],
        ["str", "Sức gốc (chỉ pet nhận mới)"],
        ["agi", "Nhanh gốc (chỉ pet nhận mới)"],
        ["_int", "Trí gốc (chỉ pet nhận mới)"],
      ],
      notes: ["Tên và hình đổi ngay cho mọi người; pet người chơi đang có giữ nguyên chỉ số."],
    });
    if (!yes) return;
    const body = {
      ...form,
      str: toNumber(form.str),
      agi: toNumber(form.agi),
      _int: toNumber(form._int),
    };
    const { ok } = await run(
      () => api.put(`/api/admin/templates/pets/${pet.petId}`, body),
      "Đã lưu pet"
    );
    if (ok) {
      onClose();
      onDone();
    }
  };

  return (
    <ActionDialog
      open
      title={`Sửa pet: ${pet.name} (#${pet.petId})`}
      color="warning"
      submitText="Lưu…"
      busy={busy}
      disabled={!form.name.trim() || !form.icon}
      onClose={onClose}
      onSubmit={submit}
    >
      <Alert severity="info">
        Tên và icon đổi ngay cho mọi người chơi. Sức / nhanh / trí gốc chỉ áp dụng cho pet nhận mới
        (hoặc trùng sinh) sau khi lưu, pet người chơi đang có giữ nguyên chỉ số.
      </Alert>
      <MDTypography variant="caption" color="text">
        Hệ {elementLabel(pet.element)} · {nclassLabel(pet.nclass)}
      </MDTypography>
      <IconPicker
        folder={folderOf(form.icon, "icons")}
        value={form.icon}
        onChange={(icon) => set({ icon })}
      />
      <MDInput
        fullWidth
        label="Tên"
        value={form.name}
        onChange={(e) => set({ name: e.target.value })}
      />
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <NumberField label="Sức" value={form.str} onChange={(str) => set({ str })} />
        </Grid>
        <Grid item xs={4}>
          <NumberField label="Nhanh" value={form.agi} onChange={(agi) => set({ agi })} />
        </Grid>
        <Grid item xs={4}>
          <NumberField label="Trí" value={form._int} onChange={(v) => set({ _int: v })} />
        </Grid>
      </Grid>
    </ActionDialog>
  );
}

PetEditDialog.propTypes = {
  pet: PropTypes.shape({
    petId: PropTypes.number.isRequired,
    name: PropTypes.string,
    icon: PropTypes.string,
    element: PropTypes.number,
    nclass: PropTypes.number,
    str: PropTypes.number,
    agi: PropTypes.number,
    _int: PropTypes.number,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

const TITLE_STATS = [
  { key: "atk", label: "Công" },
  { key: "def", label: "Thủ" },
  { key: "hp", label: "HP" },
  { key: "mp", label: "MP" },
  { key: "str", label: "Sức" },
  { key: "agi", label: "Nhanh" },
  { key: "_int", label: "Trí" },
];

export function AchievementEditDialog({ title, onClose, onDone }) {
  const [run, busy] = useRunner();
  const ask = useConfirmChanges();
  const [form, setForm] = useState({
    name: title.name || "",
    description: title.description || "",
    iconPath: title.iconPath || "",
    ...Object.fromEntries(TITLE_STATS.map((s) => [s.key, title[s.key] ?? 0])),
  });
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const submit = async () => {
    const yes = await ask({
      title: `Lưu danh hiệu ${title.name}`,
      before: title,
      after: form,
      fields: [
        ["name", "Tên"],
        ["description", "Mô tả"],
        ["iconPath", "Hình"],
        ...TITLE_STATS.map((st) => [st.key, st.label]),
      ],
      notes: ["Áp dụng ngay cho MỌI người đang có danh hiệu này (cả PvP)."],
    });
    if (!yes) return;
    const body = {
      ...form,
      ...Object.fromEntries(TITLE_STATS.map((s) => [s.key, toNumber(form[s.key])])),
    };
    const { ok } = await run(
      () => api.put(`/api/admin/templates/achievements/${title.id}`, body),
      "Đã lưu danh hiệu"
    );
    if (ok) {
      onClose();
      onDone();
    }
  };

  return (
    <ActionDialog
      open
      title={`Sửa danh hiệu: ${title.name} (#${title.id})`}
      color="warning"
      submitText="Lưu…"
      busy={busy}
      disabled={!form.name.trim() || !form.iconPath}
      onClose={onClose}
      onSubmit={submit}
    >
      <Alert severity="info">
        Áp dụng ngay cho mọi người đang đeo danh hiệu này (chỉ số tính lại khi pet cập nhật chỉ số).
      </Alert>
      <IconPicker
        folder={folderOf(form.iconPath, "achievement")}
        value={form.iconPath}
        onChange={(iconPath) => set({ iconPath })}
      />
      <MDInput
        fullWidth
        label="Tên"
        value={form.name}
        onChange={(e) => set({ name: e.target.value })}
      />
      <MDInput
        fullWidth
        multiline
        minRows={2}
        label="Mô tả"
        value={form.description}
        onChange={(e) => set({ description: e.target.value })}
      />
      <MDTypography variant="button" fontWeight="medium">
        Chỉ số cộng thêm khi đeo danh hiệu
      </MDTypography>
      <Grid container spacing={2}>
        {TITLE_STATS.map((s) => (
          <Grid item xs={6} sm={3} key={s.key}>
            <NumberField
              label={s.label}
              value={form[s.key]}
              onChange={(v) => set({ [s.key]: v })}
            />
          </Grid>
        ))}
      </Grid>
    </ActionDialog>
  );
}

AchievementEditDialog.propTypes = {
  title: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    description: PropTypes.string,
    iconPath: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

const TATTOO_STATS = [
  { key: "atk", label: "Công" },
  { key: "def", label: "Thủ" },
  { key: "hp", label: "HP" },
  { key: "mp", label: "MP" },
];

export function TattooEditDialog({ tattoo, onClose, onDone }) {
  const [run, busy] = useRunner();
  const ask = useConfirmChanges();
  const [form, setForm] = useState({
    name: tattoo.name || "",
    iconPath: tattoo.iconPath || "",
    percent: tattoo.percent ?? 100,
    ...Object.fromEntries(TATTOO_STATS.map((s) => [s.key, tattoo[s.key] ?? 0])),
  });
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const percent = Number(form.percent);
  const percentValid = percent > 0 && percent <= 100;

  const submit = async () => {
    const yes = await ask({
      title: `Lưu hình xăm ${tattoo.name}`,
      before: tattoo,
      after: form,
      fields: [
        ["name", "Tên"],
        ["iconPath", "Hình"],
        ["percent", "Tỉ lệ ra (%)"],
        ...TATTOO_STATS.map((st) => [st.key, st.label]),
      ],
      notes: ["Áp dụng ngay cho MỌI hình xăm người chơi đang có (cả PvP)."],
    });
    if (!yes) return;
    const body = {
      ...form,
      percent,
      ...Object.fromEntries(TATTOO_STATS.map((s) => [s.key, toNumber(form[s.key])])),
    };
    const { ok } = await run(
      () => api.put(`/api/admin/templates/tattoos/${tattoo.id}`, body),
      "Đã lưu hình xăm"
    );
    if (ok) {
      onClose();
      onDone();
    }
  };

  return (
    <ActionDialog
      open
      title={`Sửa hình xăm: ${tattoo.name} (#${tattoo.id})`}
      color="warning"
      submitText="Lưu…"
      busy={busy}
      disabled={!form.name.trim() || !form.iconPath || !percentValid}
      onClose={onClose}
      onSubmit={submit}
    >
      <Alert severity="info">
        Áp dụng ngay cho mọi hình xăm người chơi đang có (chỉ số pet tính lại khi pet cập nhật chỉ
        số). Mỗi cấp cường hoá +10% giá trị gốc, +10 = gấp đôi.
      </Alert>
      <IconPicker
        folder={folderOf(form.iconPath, "tatoos")}
        value={form.iconPath}
        onChange={(iconPath) => set({ iconPath })}
      />
      <MDInput
        fullWidth
        label="Tên"
        value={form.name}
        onChange={(e) => set({ name: e.target.value })}
      />
      <MDTypography variant="button" fontWeight="medium">
        Chỉ số gốc (+0)
      </MDTypography>
      <Grid container spacing={2}>
        {TATTOO_STATS.map((s) => (
          <Grid item xs={6} sm={3} key={s.key}>
            <NumberField
              label={s.label}
              value={form[s.key]}
              onChange={(v) => set({ [s.key]: v })}
            />
          </Grid>
        ))}
      </Grid>
      <MDInput
        fullWidth
        type="number"
        label="Độ dễ ra (%)"
        value={form.percent}
        inputProps={{ min: 0, max: 100, step: "any" }}
        error={!percentValid}
        helperText="Càng cao càng DỄ ra khi dùng mực xăm (bốc đều một hình rồi thử theo tỉ lệ này). Phải lớn hơn 0."
        onChange={(e) => set({ percent: e.target.value })}
      />
    </ActionDialog>
  );
}

TattooEditDialog.propTypes = {
  tattoo: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    iconPath: PropTypes.string,
    atk: PropTypes.number,
    def: PropTypes.number,
    hp: PropTypes.number,
    mp: PropTypes.number,
    percent: PropTypes.number,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};
